import { SliderComponent } from "../base/global.js";

/**
 * MulticolumnUSPSlideshowComponent extends SliderComponent to provide autoplay functionality
 * specifically for multicolumn USP sections.
 * @extends {SliderComponent}
 */
class MulticolumnUSPSlideshowComponent extends SliderComponent {
  /**
   * Initializes the multicolumn USP slideshow component.
   */
  constructor() {
    super();

    // Early return if slider is not found (SliderComponent handles this).
    if (!this.slider) return;

    this.enableSliderLooping = true;

    // Initialize reducedMotion for all multicolumn USP slideshow components.
    // This is needed because setAutoPlay() may be called even when announcementBarSlider doesn't exist.
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.reducedMotionChangeHandler = () => {
      if (this.slider?.getAttribute("data-autoplay") === "true") {
        this.setAutoPlay();
      }
    };
    this.reducedMotion.addEventListener(
      "change",
      this.reducedMotionChangeHandler,
    );

    // Initialize button click tracking.
    this.announcementBarArrowButtonWasClicked = false;

    // Track button clicks to pause autoplay when user interacts.
    if (this.prevButton && this.nextButton) {
      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    // Set up slide visibility tracking.
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    // Initialize autoplay if enabled.
    if (this.slider.getAttribute("data-autoplay") === "true") {
      this.setAutoPlay();
    }
  }

  /**
   * Sets up autoplay functionality for the slideshow.
   */
  setAutoPlay() {
    if (!this.slider) return;

    this.autoplaySpeed = (this.slider.dataset.speed || 5) * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    // Check if there's an autoplay button.
    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this),
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      // Use reducedMotion and button click state to determine if we should pause.
      const shouldPause =
        this.reducedMotion?.matches ||
        this.announcementBarArrowButtonWasClicked;
      shouldPause ? this.pause() : this.play();
    }
  }

  /**
   * Handles button clicks for navigation.
   * @param {Event} event - The click event.
   */
  onButtonClick(event) {
    super.onButtonClick(event);
    this.announcementBarArrowButtonWasClicked = true;
  }

  /**
   * Toggles autoplay on/off when autoplay button is clicked.
   */
  autoPlayToggle() {
    if (!this.sliderAutoplayButton) return;

    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  /**
   * Handles focus out events to resume autoplay if appropriate.
   * @param {Event} event - The focusout event.
   */
  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion?.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  /**
   * Handles focus in events to pause autoplay if appropriate.
   * @param {Event} event - The focusin event.
   */
  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else {
      // Pause when user interacts with the slideshow.
      this.pause();
    }
  }

  /**
   * Starts the autoplay interval.
   */
  play() {
    if (!this.slider) return;

    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed,
    );
  }

  /**
   * Stops the autoplay interval.
   */
  pause() {
    if (!this.slider) return;

    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  /**
   * Toggles the visual state of the autoplay button.
   * @param {boolean} pauseAutoplay - Whether autoplay is paused.
   */
  togglePlayButtonState(pauseAutoplay) {
    if (!this.sliderAutoplayButton) return;

    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings?.playSlideshow || "Play slideshow",
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings?.pauseSlideshow || "Pause slideshow",
      );
    }
  }

  /**
   * Automatically rotates slides at the configured interval.
   */
  autoRotateSlides() {
    if (!this.slider || !this.sliderItemsToShow?.length) return;

    const slideScrollPosition =
      this.currentPage === this.sliderItemsToShow.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
  }

  /**
   * Updates slide visibility and accessibility attributes.
   */
  setSlideVisibility() {
    if (!this.sliderItemsToShow?.length) return;

    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length) {
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        }
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length) {
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        }
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
  }

  /**
   * Cleans up event listeners and intervals when component is disconnected.
   */
  disconnectedCallback() {
    clearInterval(this.autoplay);
    if (this.reducedMotion && this.reducedMotionChangeHandler) {
      this.reducedMotion.removeEventListener(
        "change",
        this.reducedMotionChangeHandler,
      );
    }
  }
}

customElements.define(
  "multicolumn-usp-slideshow-component",
  MulticolumnUSPSlideshowComponent,
);
